// v187 admin notification-channels API client. Companion to
// notifications.ts (which keeps the SMTP/email surface).
//
// Endpoints:
//   GET    /api/admin/notification-channels
//   POST   /api/admin/notification-channels
//   PUT    /api/admin/notification-channels/{id}
//   DELETE /api/admin/notification-channels/{id}
//   POST   /api/admin/notification-channels/{id}/test

import useSWR from 'swr';

export type ChannelKind = 'teams' | 'slack' | 'webhook';

export interface NotificationChannel {
  id: string;
  kind: ChannelKind;
  name: string;
  webhook_url: string; // server returns a masked form on list/get
  enabled: boolean;
  events: string[];
  project_id?: string | null;
  created_at: string;
  created_by: string;
  updated_at: string;
  last_test_at?: string | null;
  last_test_ok?: boolean | null;
  last_error: string;
}

export interface ChannelsListResponse {
  channels: NotificationChannel[];
}

const fetcher = (url: string) =>
  fetch(url, { credentials: 'include' }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

export function useNotificationChannels() {
  return useSWR<ChannelsListResponse>('/api/admin/notification-channels', fetcher);
}

export interface CreateChannelInput {
  kind: ChannelKind;
  name: string;
  webhook_url: string;
  enabled?: boolean;
  events?: string[];
  project_id?: string | null;
}

export async function createChannel(input: CreateChannelInput): Promise<NotificationChannel> {
  const res = await fetch('/api/admin/notification-channels', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export interface UpdateChannelInput {
  name?: string;
  webhook_url?: string;
  enabled?: boolean;
  events?: string[];
  project_id?: string;
  clear_project?: boolean;
}

export async function updateChannel(id: string, input: UpdateChannelInput): Promise<NotificationChannel> {
  const res = await fetch(`/api/admin/notification-channels/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function deleteChannel(id: string): Promise<void> {
  const res = await fetch(`/api/admin/notification-channels/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw new Error(await res.text());
}

export interface TestResult {
  ok: boolean;
  channel: string;
  error?: string;
}

export async function testChannel(id: string): Promise<TestResult> {
  const res = await fetch(`/api/admin/notification-channels/${encodeURIComponent(id)}/test`, {
    method: 'POST',
    credentials: 'include',
  });
  // Both 200 (ok) and 502 (delivery failed but recorded) carry a JSON body.
  const body = await res.json().catch(() => ({}));
  return body as TestResult;
}
